import { BookUp, ChevronLeft, ChevronRight, Clock, Copy, ExternalLink, Link as LinkIcon, MoreHorizontal, Pencil, Pin, Play, SquareCheck, Trash2, Trophy } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import CategoryPicker from './category-picker';

import type { ExperienceSummary, KanbanStatus } from '@/types/experience';

import { POPUP_LEVEL } from '@/components/ui/popup-level';
import { relativeTime } from '@/lib/relative-time';


interface ExperienceCardProps {
  experience: ExperienceSummary;
  kanbanStatus: KanbanStatus;
  onEdit: () => void;
  onSeeResults: () => void;
  onPreview: () => void;
  onTogglePin: () => void;
  onDuplicate: () => void;
  onRemove: () => void;
  onCopyLink?: () => void;
  /** Move one pipeline stage forward / back — shown as ◀ ▶ in the card header. Omit an end (no
   * forward on Deployed, no back on In Development). Forward on Complete opens the Publish flow. */
  onProgress?: () => void;
  progressLabel?: string;
  /** 'publish' renders the forward control as a publish action (distinct icon + accent) rather than
   * a plain move-to-next chevron, since Complete → Deployed triggers the publish flow. */
  progressVariant?: 'move' | 'publish';
  onRegress?: () => void;
  regressLabel?: string;
  /** Persist an inline-edited summary. When provided, the summary line becomes click-to-edit. */
  onSummarySave?: (summary: string) => void;
  /** Inline single-category control (the structural value the board groups by). */
  categoryOptions?: string[];
  onCategoryChange?: (value: string) => void;
}

export default function ExperienceCard({
  experience,
  kanbanStatus,
  onEdit,
  onSeeResults,
  onPreview,
  onTogglePin,
  onDuplicate,
  onRemove,
  onCopyLink,
  onProgress,
  progressLabel,
  progressVariant = 'move',
  onRegress,
  regressLabel,
  onSummarySave,
  categoryOptions,
  onCategoryChange,
}: ExperienceCardProps) {
  const { t, i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top?: number; bottom?: number; right: number }>({ right: 0 });
  const [editingSummary, setEditingSummary] = useState(false);
  const [summaryDraft, setSummaryDraft] = useState('');
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const summaryRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (editingSummary && summaryRef.current) {
      const el = summaryRef.current;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    }
  }, [editingSummary]);

  function close() { setMenuOpen(false); }

  function startEditSummary(e: React.MouseEvent) {
    e.stopPropagation();
    setSummaryDraft(experience.summary ?? '');
    setEditingSummary(true);
  }
  function commitSummary() {
    setEditingSummary(false);
    const next = summaryDraft.trim();
    if (next !== (experience.summary ?? '')) onSummarySave?.(next);
  }

  const MENU_HEIGHT = 380;

  function openMenu(e: React.MouseEvent) {
    e.stopPropagation();
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const right = window.innerWidth - rect.right;
    const spaceBelow = window.innerHeight - rect.bottom;
    if (spaceBelow < MENU_HEIGHT) {
      setMenuPos({ bottom: window.innerHeight - rect.top + 4, right });
    } else {
      setMenuPos({ top: rect.bottom + 4, right });
    }
    setMenuOpen((o) => !o);
  }

  useEffect(() => {
    if (!menuOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node) &&
          btnRef.current && !btnRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    function handleScroll() { setMenuOpen(false); }
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [menuOpen]);

  const dropdown = menuOpen && createPortal(
    <div
      ref={menuRef}
      style={{ top: menuPos.top, bottom: menuPos.bottom, right: menuPos.right }}
      className={`fixed ${POPUP_LEVEL} w-44 rounded-xl border border-ws-border bg-ws-elevated p-1 font-medium shadow-lg`}
    >
      {kanbanStatus === 'live' && (
        <button onClick={(e) => { e.stopPropagation(); onSeeResults(); close(); }} className='flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-ws-text hover:bg-ws-hover'>
          <ExternalLink className='h-4 w-4 shrink-0 text-ws-muted' />
          {t('experience.card.seeResults')}
        </button>
      )}
      {onCopyLink && (
        <button onClick={(e) => { e.stopPropagation(); onCopyLink(); close(); }} className='flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-ws-text hover:bg-ws-hover'>
          <LinkIcon className='h-4 w-4 shrink-0 text-ws-muted' />
          {t('experience.card.link')}
        </button>
      )}
      <button onClick={(e) => { e.stopPropagation(); onDuplicate(); close(); }} className='flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-ws-text hover:bg-ws-hover'>
        <Copy className='h-4 w-4 shrink-0 text-ws-muted' />
        {t('common.duplicate')}
      </button>
      <div className='my-1 border-t border-ws-border' />
      <button onClick={(e) => { e.stopPropagation(); onRemove(); close(); }} className='flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-ws-error-fg hover:bg-ws-hover'>
        <Trash2 className='h-4 w-4 shrink-0' />
        {t('common.delete')}
      </button>
    </div>,
    document.body
  );

  return (
    <div
      role='button'
      tabIndex={0}
      onClick={onEdit}
      onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onEdit(); } }}
      className='group hover-card hover-card-raise relative flex cursor-pointer flex-col gap-2 rounded-xl border border-ws-border bg-ws-surface p-3 shadow-[0px_1px_2px_0px_#0000000D] focus-visible:outline focus-visible:outline-2 focus-visible:outline-ws-primary'
    >
      {/* Header: pin (leading toggle) · title · actions (◀ regress ▶ progress · preview · menu) */}
      <div className='flex items-center gap-1.5'>
        <button
          onClick={(e) => { e.stopPropagation(); onTogglePin(); }}
          aria-label={experience.pinned ? t('experience.card.unpin') : t('experience.card.pin')}
          title={experience.pinned ? t('experience.card.unpin') : t('experience.card.pin')}
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded transition-colors ${experience.pinned ? 'text-ws-secondary' : 'text-ws-muted opacity-40 hover:opacity-100'}`}
        >
          <Pin className='h-4 w-4 rotate-45' strokeWidth={1.75} />
        </button>
        <button onClick={(e) => { e.stopPropagation(); onPreview(); }} aria-label={t('experience.card.preview')} title={t('experience.card.preview')}
          className='flex h-5 w-5 shrink-0 items-center justify-center rounded text-ws-muted hover:text-ws-text'>
          <Play className='h-4 w-4' />
        </button>
        <p className='min-w-0 flex-1 truncate text-md font-medium text-ws-text' title={experience.title}>{experience.title}</p>
        <div className='flex shrink-0 items-center gap-0.5'>
          {onRegress && (
            <button onClick={(e) => { e.stopPropagation(); onRegress(); }} aria-label={regressLabel} title={regressLabel}
              className='flex h-7 w-6 items-center justify-center rounded-lg text-ws-muted hover:bg-ws-hover hover:text-ws-text'>
              <ChevronLeft className='h-4 w-4' />
            </button>
          )}
          {onProgress && (
            <button onClick={(e) => { e.stopPropagation(); onProgress(); }} aria-label={progressLabel} title={progressLabel}
              className={`flex h-7 w-6 items-center justify-center rounded-lg hover:bg-ws-hover ${progressVariant === 'publish' ? 'text-ws-text-link' : 'text-ws-muted hover:text-ws-text'}`}>
              {progressVariant === 'publish' ? <BookUp className='h-4 w-4' /> : <ChevronRight className='h-4 w-4' />}
            </button>
          )}
          <button ref={btnRef} onClick={openMenu} aria-label={t('experience.card.menu')}
            className='flex h-7 w-7 items-center justify-center rounded-lg text-ws-text hover:bg-ws-hover'>
            <MoreHorizontal className='h-4 w-4' />
          </button>
          {dropdown}
        </div>
      </div>

      {/* Summary — inline editable when onSummarySave is provided */}
      {editingSummary ? (
        <textarea
          ref={summaryRef}
          rows={2}
          maxLength={500}
          value={summaryDraft}
          placeholder={t('experience.card.addSummary')}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => setSummaryDraft(e.target.value)}
          onBlur={commitSummary}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commitSummary(); }
            else if (e.key === 'Escape') { e.preventDefault(); setEditingSummary(false); }
          }}
          className='w-full resize-none rounded-md border border-ws-border bg-ws-bg px-2 py-1 text-xs text-ws-text focus:border-ws-primary focus:outline-none'
        />
      ) : onSummarySave ? (
        <button onClick={startEditSummary} title={t('experience.card.editSummary')} className='group/sum -mx-0.5 flex items-start gap-1 rounded-md px-0.5 py-0.5 text-left hover:bg-ws-hover'>
          {experience.summary
            ? <span className='line-clamp-2 min-w-0 flex-1 break-words text-xs text-ws-muted'>{experience.summary}</span>
            : <span className='min-w-0 flex-1 text-xs italic text-ws-text-disabled underline decoration-dotted underline-offset-2'>{t('experience.card.addSummary')}</span>}
          <Pencil className='mt-0.5 h-3 w-3 shrink-0 text-ws-muted opacity-0 transition-opacity group-hover/sum:opacity-100' />
        </button>
      ) : experience.summary ? (
        <p className='line-clamp-2 break-words text-xs text-ws-muted'>{experience.summary}</p>
      ) : (
        <p className='text-xs italic text-ws-text-disabled'>{t('experience.card.noSummary')}</p>
      )}

      {/* Category (inline, first) · tasks · points · edited. Wraps so it never spills past the card
          edge in narrow columns (landscape / tablet-portrait 3-up). */}
      <div className='flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ws-muted'>
        {onCategoryChange ? (
          <CategoryPicker value={experience.category ?? ''} options={categoryOptions ?? []} onChange={onCategoryChange} />
        ) : experience.category ? (
          <span className='rounded border border-ws-text-link/50 px-2 py-0.5 text-sm font-medium leading-none text-ws-text-link'>
            {experience.category}
          </span>
        ) : null}
        <span className='flex items-center gap-1'>
          <SquareCheck className='h-3.5 w-3.5' />
          {experience.taskCount} {t('experience.card.task', { count: experience.taskCount })}
        </span>
        <span className='flex items-center gap-1'>
          <Trophy className='h-3.5 w-3.5' />
          {experience.totalPoints ?? 0} {t('experience.card.points')}
        </span>
        <span className='ml-auto flex items-center gap-1' title={t('experience.card.editedAt', { date: new Date(experience.updatedAt).toLocaleString() })}>
          <Clock className='h-3.5 w-3.5' />
          {relativeTime(experience.updatedAt, i18n.language)}
        </span>
      </div>
    </div>
  );
}
